import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SEO from "@/components/feature/SEO";
import SchemaOrg from "@/components/feature/SchemaOrg";
import { categories } from "@/mocks/categories";
import { getComicsByCategory, type Comic } from "@/mocks/comics";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || "rexue");
  const [comics, setComics] = useState<Comic[]>([]);
  const [sortBy, setSortBy] = useState<"latest" | "rating" | "chapters">("latest");

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    setComics(getComicsByCategory(activeSlug));
  }, [activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);

  const sortedComics = [...comics].sort((a, b) => {
    if (sortBy === "rating") return b.rating - a.rating;
    if (sortBy === "chapters") return b.chapters - a.chapters;
    return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
  });

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${activeCategory?.name || "漫画"}分类 - 51漫画免费下载 | 51漫画网站`}
        description={`51漫画${activeCategory?.name || ""}分类页面，提供海量${activeCategory?.name || ""}漫画免费下载与离线阅读，支持手机客户端安装，随时随地畅享${activeCategory?.name || ""}漫画阅读。`}
        keywords={`51漫画${activeCategory?.name || ""}下载,51漫画免费下载,51漫画${activeCategory?.name || ""}漫画,51漫画APP下载,离线漫画,免费漫画下载`}
        canonicalUrl={`https://www.51manhua.com/category/${activeSlug}`}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${activeCategory?.name || "漫画"}分类 - 51漫画`,
          description: `51漫画${activeCategory?.name || ""}分类，海量${activeCategory?.name || ""}漫画免费下载`,
          url: `https://www.51manhua.com/category/${activeSlug}`,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://www.51manhua.com/" },
            { "@type": "ListItem", position: 2, name: "漫画分类", item: "https://www.51manhua.com/category/rexue" },
            { "@type": "ListItem", position: 3, name: activeCategory?.name || "漫画" },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: "漫画分类", href: "/category/rexue" },
                  { label: activeCategory?.name || "漫画" },
                ]}
              />
            </div>

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              {activeCategory?.name}漫画
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              共 {comics.length} 部{activeCategory?.name}漫画资源，支持免费下载与离线阅读
            </p>

            <div className="flex flex-col lg:flex-row gap-6">
              <aside className="lg:w-52 flex-shrink-0">
                <div className="sticky top-24">
                  <h3 className="text-sm font-semibold text-foreground-700 mb-3">分类筛选</h3>
                  <div className="space-y-1">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        onClick={() => setActiveSlug(cat.slug)}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap ${
                          activeSlug === cat.slug
                            ? "bg-primary-50 text-primary-700 font-medium"
                            : "text-foreground-600 hover:bg-background-100"
                        }`}
                      >
                        <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${
                          activeSlug === cat.slug ? "bg-primary-500 text-white" : "bg-background-200 text-foreground-500"
                        }`}>
                          <i className={`${cat.icon} text-xs`} />
                        </div>
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-5">
                  <p className="text-sm text-foreground-500">
                    共 <strong className="text-foreground-800">{sortedComics.length}</strong> 部作品
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500">排序:</span>
                    <div className="flex bg-background-200/60 rounded-full p-0.5">
                      {(["latest", "rating", "chapters"] as const).map((key) => (
                        <button
                          key={key}
                          onClick={() => setSortBy(key)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                            sortBy === key
                              ? "bg-background-50 text-foreground-900"
                              : "text-foreground-500 hover:text-foreground-700"
                          }`}
                        >
                          {key === "latest" ? "最新" : key === "rating" ? "评分" : "话数"}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop>
                  {sortedComics.map((comic) => (
                    <Link
                      key={comic.id}
                      to={`/article/${comic.id}`}
                      className="group block"
                    >
                      <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-background-200 mb-3">
                        <img
                          src={comic.cover}
                          alt={comic.title}
                          title={`${comic.title} - 51漫画免费下载`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                        <div className="absolute top-2 left-2">
                          <span className="px-2 py-0.5 bg-primary-500 text-white text-xs font-medium rounded-full">
                            {comic.rating}
                          </span>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                          <span className="text-white text-xs">{comic.chapters}话</span>
                        </div>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
                    </Link>
                  ))}
                </div>

                {sortedComics.length === 0 && (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-200 text-foreground-400 mb-4">
                      <i className="ri-inbox-line text-2xl" />
                    </div>
                    <p className="text-foreground-500">该分类暂无漫画资源</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}